import React, { useEffect, useState } from 'react';

import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import MetaTags from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Nav, NavItem, NavLink, Row, Pagination } from 'reactstrap';

import { ticketService } from '../../services/ticketService';
import { fetchTickets } from '../../store/tickets/actions';

import CreateTicketModal from './components/CreateTicketModal';
import TicketList from './components/TicketList';

import TicketPage from './TicketPage';
import './support.scss';

const SupportPage = () => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const [activeTab, setActiveTab] = useState('recent');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [pagination, setPagination] = useState({});

  const dispatch = useDispatch();
  const { tickets = [], total_pages_count = 0, total_items_count = 0 } = useSelector(state => ({
    tickets: state.Tickets.tickets || [],
    total_pages_count: state.Tickets.total_pages_count || 0,
    total_items_count: state.Tickets.total_items_count || 0
  }));

  useEffect(() => {
    fetchStatesAndPriorities();
  }, []);

  useEffect(() => {
    if (!pagination.pageNumber) {
      setPagination({ pageNumber: 1, size: 25 });
    }
  }, []);

  useEffect(() => {
    const getFilterState = () => {
      switch (activeTab) {
        case 'recent':
          return ['new', 'open', 'pending reminder', 'pending close', 'closed', 'merged'];
        case 'open':
          return ['open'];
        case 'closed':
          return ['closed', 'merged'];
        default:
          return [];
      }
    };

    if (pagination?.pageNumber) {
      dispatch(fetchTickets({
        filter_state: getFilterState(),
        take: pagination.size,
        skip: (pagination.pageNumber - 1) * pagination.size,
        sort_by: 'created_at',
        order_by: 'desc'
      }));
    }
  }, [dispatch, activeTab, pagination]);

  const getTicketCounts = () => {
    const recentTickets = tickets.filter(ticket => [1, 2, 3, 4, 5, 6].includes(ticket.state_id)); 
    const openTickets = tickets.filter(ticket => ticket.state_id === 2); 
    const closedTickets = tickets.filter(ticket => [4,5].includes(ticket.state_id));
    return {
      recent: recentTickets.length,
      open: openTickets.length,
      closed: closedTickets.length
    };
  };

  const hasUnreadMessages = (tabName) => {
    let filteredTickets;
    switch (tabName) {
      case 'recent':
        filteredTickets = tickets.filter(ticket => [1, 2, 3, 4, 5, 6].includes(ticket.state_id));
        break;
      case 'open':
        filteredTickets = tickets.filter(ticket => ticket.state_id === 2);
        break;
      case 'closed':
        filteredTickets = tickets.filter(ticket => [4,5].includes(ticket.state_id));
        break;
      default:
        return false;
    }
    return filteredTickets.some(ticket => ticket.unread_messages);
  };

  const ticketCounts = getTicketCounts();

  const fetchStatesAndPriorities = async () => {
    try {
      const [statesResponse, prioritiesResponse] = await Promise.all([
        ticketService.getTicketStates(),
        ticketService.getTicketPriorities(),
      ]);
      setStates(statesResponse);
      setPriorities(prioritiesResponse);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching states and priorities:', error);
      setLoading(false);
    }
  };

  const handleCreateTicketSuccess = () => {
    dispatch(fetchTickets({
      filter_state: activeTab === 'recent' ? ['new', 'open', 'pending reminder', 'pending close', 'closed', 'merged'] : [activeTab],
      take: pagination.size,
      skip: (pagination.pageNumber - 1) * pagination.size
    }));
  };

  const handleCreateTicket = async (data) => {
    try {
      await data;
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, pageNumber: 1 })); // Сбрасываем на первую страницу при смене таба
  };

  const toggle = () => setIsModalOpen(!isModalOpen);
console.log(total_items_count)
  return (
    <div className="page-content">
      <MetaTags>
        <title>{t('support_title')} | MC</title>
      </MetaTags>

      <Container fluid>
        <Row>
          <Col xs={12}>
            <Switch>
              <Route exact path={path}>
                <Card>
                  <CardBody>
                    <div className="support-header support-small-margin">
                      <div className="support-header__text">
                        <h4 className="support-header__title">{t('support_title')}</h4>
                        <p className="support-header__subtitle d-none d-md-block">
                          {t('support_description')}
                        </p>
                      </div>
                      <Button
                        className="support-header__create-btn"
                        onClick={() => setIsModalOpen(true)}>
                        + {t('support_create_ticket')}
                      </Button>
                    </div>

                    <div className="status-tabs support-small-margin">
                      <div className="tabs-container">
                        <div
                          className={`tab-item ${activeTab === 'recent' ? 'active' : ''}`}
                          onClick={() => handleTabChange('recent')}>
                          <div className="d-flex align-items-center">
                            {t('support_tabs_recent')}
                            {hasUnreadMessages('recent') && <span className="unread-dot" />}
                          </div>
                        </div>
                        <div
                          className={`tab-item ${activeTab === 'open' ? 'active' : ''}`}
                          onClick={() => handleTabChange('open')}>
                          <div className="d-flex align-items-center">
                            {t('support_tabs_open')}
                            {hasUnreadMessages('open') && <span className="unread-dot" />}
                          </div>
                        </div>
                        <div
                          className={`tab-item ${activeTab === 'closed' ? 'active' : ''}`}
                          onClick={() => handleTabChange('closed')}>
                          <div className="d-flex align-items-center">
                            {t('support_tabs_closed')}
                            {hasUnreadMessages('closed') && <span className="unread-dot" />}
                          </div>
                        </div>
                      </div>
                    </div>

                    <TicketList
                      tickets={tickets}
                      loading={loading}
                      activeTab={activeTab}
                      states={states}
                      priorities={priorities}
                      total={total_pages_count}
                      totalItems={total_items_count}
                      setPagination={setPagination}
                      pagination={pagination}
                      />
                  </CardBody>
                </Card>
              </Route>
              <Route path={`${path}/ticket/:id`}>
                <TicketPage />
              </Route>
            </Switch>

            <CreateTicketModal
              isOpen={isModalOpen}
              toggle={toggle}
              onSubmit={handleCreateTicket}
              onSuccess={handleCreateTicketSuccess}
              priorities={priorities}
              ticketService={ticketService}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SupportPage;
